// src/pages/SettingsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useAuth } from "@/context/AuthContext";
import {
    getAuth,
    reauthenticateWithCredential,
    EmailAuthProvider,
    updatePassword,
} from "firebase/auth";

import UserInfoCard from "@/components/UserInfo";
import ProfileUpdateForm from "@/components/Forms/ProfileUpdateForm";
import PasswordUpdateForm from "@/components/Forms/PasswordUpdateForm";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
    Settings,
    ShieldCheck,
    UserRound,
    BadgeCheck,
    Sparkles,
} from "lucide-react";

export default function SettingsPage() {
    const { user, updateUserClaims, fetchUserRole, claims } = useAuth();

    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const [isLoadingPassword, setIsLoadingPassword] = useState(false);
    const [showPasswords, setShowPasswords] = useState({ old: false, new: false });

    const auth = getAuth();
    const [hasPassword, setHasPassword] = useState(false);

    useEffect(() => {
        if (user) {
            const providers = user.providerData.map((p) => p.providerId);
            setHasPassword(providers.includes("password"));
        }
    }, [user]);

    const togglePasswordVisibility = (field: "old" | "new") => {
        setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const profileForm = useForm({
        defaultValues: {
            displayName: user?.displayName || "",
            birthDate: claims?.birthDate || "",
            gender: claims?.gender || "",
        },
    });

    const passwordForm = useForm({
        defaultValues: {
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
    });

    const handleProfileUpdate = async (data: {
        displayName: string;
        birthDate: string;
        gender: string;
    }) => {
        setIsLoadingProfile(true);
        try {
            await updateUserClaims(data.gender, data.birthDate, data.displayName);
            if (user) await fetchUserRole(user);
            await auth.currentUser?.reload();

            profileForm.reset(data);
            toast.success("Профиль успешно обновлен!");
        } catch (error) {
            toast.error("Ошибка обновления профиля.");
            console.error(error);
        } finally {
            setIsLoadingProfile(false);
        }
    };

    const handlePasswordUpdate = async (data: {
        oldPassword?: string;
        newPassword: string;
        confirmPassword: string;
    }) => {
        if (data.newPassword !== data.confirmPassword) {
            passwordForm.setError("confirmPassword", { message: "Пароли не совпадают!" });
            return;
        }

        setIsLoadingPassword(true);
        try {
            if (!user) throw new Error("Пользователь не авторизован.");

            const credential = EmailAuthProvider.credential(user.email!, data.oldPassword!);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, data.newPassword);

            toast.success("Пароль успешно обновлен!");
            passwordForm.reset();
            setShowPasswords({ old: false, new: false });
        } catch (error: any) {
            if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
                passwordForm.setError("oldPassword", { message: "Неверный старый пароль!" });
            } else if (error.code === "auth/weak-password") {
                passwordForm.setError("newPassword", { message: "Слишком слабый новый пароль!" });
            } else {
                toast.error("Ошибка смены пароля.");
            }
            console.error("Ошибка смены пароля:", error);
        } finally {
            setIsLoadingPassword(false);
        }
    };

    useEffect(() => {
        if (user && claims) {
            profileForm.reset({
                displayName: user.displayName || "",
                birthDate: claims.birthDate || "",
                gender: claims.gender || "Мужчина",
            });
        }
    }, [user, claims, profileForm]);

    const statusLabel = useMemo(() => {
        return hasPassword ? "Пароль подключен" : "Только соц вход";
    }, [hasPassword]);

    return (
        <div className="min-h-[100dvh] bg-background">
            <div className="mx-auto w-full max-w-6xl px-4 py-6">
                {/* Top header */}
                <Card className="overflow-hidden">
                    <CardHeader className="relative">
                        {/* soft background */}
                        <div className="pointer-events-none absolute inset-0 opacity-70">
                            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-muted blur-3xl" />
                            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-muted blur-3xl" />
                        </div>

                        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-3">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border bg-muted/30">
                                    <Settings className="h-5 w-5 text-foreground/80" />
                                </div>

                                <div className="space-y-1">
                                    <CardTitle className="text-xl font-semibold tracking-tight">
                                        Настройки
                                    </CardTitle>
                                    <div className="text-sm text-muted-foreground">
                                        Профиль, доступы и безопасность аккаунта
                                    </div>

                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <Badge variant="secondary" className="rounded-full">
                                            <BadgeCheck className="mr-2 h-4 w-4" />
                                            {statusLabel}
                                        </Badge>

                                        <Badge variant="outline" className="rounded-full">
                                            <Sparkles className="mr-2 h-4 w-4" />
                                            Черный и белый режим
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        {/* User summary */}
                        <div className="rounded-xl border bg-muted/20 p-3">
                            <UserInfoCard user={user} />
                        </div>

                        <Separator />

                        {/* Sections */}
                        <div
                            className={[
                                "grid gap-4",
                                hasPassword ? "md:grid-cols-2" : "md:grid-cols-1",
                            ].join(" ")}
                        >
                            {/* Profile section */}
                            <Card className="border bg-background">
                                <CardHeader className="pb-3">
                                    <div className="flex items-start gap-3">
                                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-muted/30">
                                            <UserRound className="h-4 w-4 text-foreground/80" />
                                        </div>

                                        <div className="space-y-1">
                                            <div className="text-sm font-semibold">Профиль</div>
                                            <div className="text-xs text-muted-foreground">
                                                Имя, пол и дата рождения
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardContent>
                                    <ProfileUpdateForm
                                        user={user}
                                        isLoadingProfile={isLoadingProfile}
                                        form={profileForm}
                                        handleProfileUpdate={handleProfileUpdate}
                                        handlePasswordResetRequest={handlePasswordUpdate}
                                        hasPassword={hasPassword}
                                    />
                                </CardContent>
                            </Card>

                            {/* Security section */}
                            {hasPassword ? (
                                <Card className="border bg-background">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-start gap-3">
                                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-muted/30">
                                                <ShieldCheck className="h-4 w-4 text-foreground/80" />
                                            </div>

                                            <div className="space-y-1">
                                                <div className="text-sm font-semibold">Безопасность</div>
                                                <div className="text-xs text-muted-foreground">
                                                    Смена пароля и защита аккаунта
                                                </div>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <CardContent>
                                        <PasswordUpdateForm
                                            showPasswords={showPasswords}
                                            isLoadingPassword={isLoadingPassword}
                                            togglePasswordVisibility={togglePasswordVisibility}
                                            form={passwordForm}
                                            handlePasswordUpdate={handlePasswordUpdate}
                                        />
                                    </CardContent>
                                </Card>
                            ) : null}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
